/* eslint-disable sonarjs/no-duplicate-string */
import {
  alphanumericAndAmp,
  convertDate,
  getFormattedDate,
  isValidEmail,
} from "../../helpers";
import { getValidCustomizations, maxLengthValidation } from "./intakeForm";
import { filterListByKeywordGivenKeyNames } from "./objects";

export const PAGE_MODES = {
  invalid: "invalid",
  create: "create",
  createWithProposal: "create-with-proposal",
  viewDetails: "view-details",
};

const PROPOSAL_EQUITY_SCENARIOS = [
  { label: "Balanced", value: "Balanced" },
  { label: "Cash Proposal", value: "Cash Proposal" },
  { label: "Min Tax Liability", value: "Min Tax Liability" },
  { label: "Min Tracking Error", value: "Min Tracking Error" },
];

const PROPOSAL_BOND_SCENARIOS = [
  ...PROPOSAL_EQUITY_SCENARIOS,
  { label: "Reinvest Proceeds", value: "Reinvest Proceeds" },
];

const DEFAULT_CARDS_METADATA = {
  ClientInfoCard: {
    id: "ClientInfoCard",
    headerNumber: "1",
    fields: ["clientName", "accountNumber"],
  },
  FinancialAdvisorInfoCard: {
    id: "FinancialAdvisorInfoCard",
    headerNumber: "2",
    fields: ["advisorName", "advisorEmail", "advisorPhoneNum"],
  },
  StrategyInformationCard: {
    id: "StrategyInformationCard",
    headerNumber: "3",
    fields: [
      "strategy",
      "statePreferenceOption",
      "taxLossHarvesting",
      "transitionPlanReportId",
      "selectScenario",
      "remiIntakeChanged",
      "updatedmaxRealizedSTGainsAllowed",
      "updatedCashToRaise",
    ],
  },
  WithdrawalsCard: {
    id: "WithdrawalsCard",
    headerNumber: "4",
    fields: ["clientWithdrawals"],
  },
  AdditionalNotesCard: {
    id: "AdditionalNotesCard",
    headerNumber: "5",
    fields: ["additionalNotes"],
  },
  InlineDisclosuresCard: {
    id: "InlineDisclosuresCard",
  },
  SignAndCompleteCard: {
    id: "SignAndCompleteCard",
    headerNumber: "6",
    fields: ["financialRepSignature", "submittedDate"],
  },
};

const DEFAULT_CARDS_LOCKED_STATE_BY_PAGE_MODE = {
  [PAGE_MODES.create]: {
    ClientInfoCard: false,
    FinancialAdvisorInfoCard: false,
    StrategyInformationCard: false,
    WithdrawalsCard: false,
    AdditionalNotesCard: false,
    InlineDisclosuresCard: false,
    SignAndCompleteCard: false,
  },
  [PAGE_MODES.createWithProposal]: {
    ClientInfoCard: false,
    FinancialAdvisorInfoCard: false,
    StrategyInformationCard: false,
    WithdrawalsCard: false,
    AdditionalNotesCard: false,
    InlineDisclosuresCard: false,
    SignAndCompleteCard: false,
  },
  [PAGE_MODES.viewDetails]: {
    ClientInfoCard: true,
    FinancialAdvisorInfoCard: true,
    StrategyInformationCard: true,
    WithdrawalsCard: true,
    AdditionalNotesCard: true,
    InlineDisclosuresCard: true,
    SignAndCompleteCard: true,
  },
  [PAGE_MODES.invalid]: {},
};

const numberPlaceholder = "Enter a number";
const onChange = (e) => {
  const value = e.target.value;
  e.target.value = alphanumericAndAmp(value);
};
const DEFAULT_FIELDS_STATES = {
  //! ClientInfoCard
  clientName: {
    type: "text",
    key: "clientName",
    name: "clientName",
    label: "Client Name",
    isVisible: true,
    options: {
      placeholder: "First Name, Last Name",
      required: true,
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },
  accountNumber: {
    type: "text",
    key: "accountNumber",
    name: "accountNumber",
    label: "Account Number",
    isVisible: true,
    options: {
      placeholder: "Account Number",
      required: true,
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },

  //! FinancialAdvisorInfoCard
  advisorName: {
    type: "text",
    key: "advisorName",
    name: "advisorName",
    label: "Financial Advisor Name",
    isVisible: true,
    options: {
      placeholder: "First Name, Last Name",
      required: true,
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },
  advisorEmail: {
    type: "text",
    key: "advisorEmail",
    name: "advisorEmail",
    label: "Email",
    isVisible: true,
    options: {
      placeholder: "Enter Email",
      required: true,
      maxLength: maxLengthValidation(50),
      setValueAs: (v) => (v.trim().length > 0 ? v : ""),
      validate: (e) => isValidEmail(e) || "Invalid Email Address",
    },
  },
  advisorPhoneNum: {
    type: "text",
    key: "advisorPhoneNum",
    name: "advisorPhoneNum",
    label: "Phone",
    isVisible: true,
    options: {
      placeholder: "Phone",
      required: true,
      maxLength: maxLengthValidation(20),
      onChange,
    },
  },

  //! StrategyInformationCard
  strategyName: {
    type: "text",
    key: "strategyName",
    name: "strategyName",
    label: "Strategy",
    isVisible: true,
    options: {
      placeholder: "Strategy",
      required: true,
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },
  statePreferenceOption: {
    type: "text",
    key: "statePreferenceOption",
    name: "statePreferenceOption",
    label: "State or Region Preferences",
    isVisible: true,
    options: {
      placeholder: "State or Region Preferences",
      required: false,
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },
  taxLossHarvesting: {
    type: "radio",
    key: "taxLossHarvesting",
    name: "taxLossHarvesting",
    isVisible: false,
    layout: "horizontal",
    fieldIndex: "1",
    options: {
      required: true,
      options: [
        {
          label:
            "Allspring <b>should systematically harvest tax losses</b> throughout the year",
          value: "yes",
        },
        {
          label:
            "Client <b>will notify Allspring</b> when they want losses harvested",
          value: "no",
        },
      ],
    },
  },
  transitionPlanReportId: {
    type: "text",
    key: "transitionPlanReportId",
    name: "transitionPlanReportId",
    label: "Transition Plan Report ID #",
    staticHelperText:
      "Transition plan report identification number is located on cover",
    isVisible: true,
    isIconLock: true,
    options: {
      readOnly: true,
      placeholder: "Enter Transition Plan Report ID",
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },
  selectScenario: {
    type: "select",
    key: "selectScenario",
    name: "selectScenario",
    label: "Scenario",
    isVisible: true,
    options: {
      required: true,
      readOnly: false,
      placeholder: "Scenario",
      items: [],
    },
  },
  remiIntakeChanged: {
    type: "radio",
    key: "remiIntakeChanged",
    name: "remiIntakeChanged",
    isVisible: true,
    layout: "horizontal",
    fieldIndex: "2",
    options: {
      required: true,
      options: [
        {
          label: "Yes",
          value: "yes",
        },
        {
          label: "No",
          value: "no",
        },
      ],
    },
  },
  updatedmaxRealizedSTGainsAllowed: {
    type: "text",
    key: "updatedmaxRealizedSTGainsAllowed",
    name: "updatedmaxRealizedSTGainsAllowed",
    label: "Max Realized Short-Term Gains Allowed",
    isVisible: false,
    options: {
      placeholder: numberPlaceholder,
      required: false,
    },
  },
  updatedCashToRaise: {
    type: "text",
    key: "updatedCashToRaise",
    name: "updatedCashToRaise",
    label: "Cash to Raise",
    isVisible: false,
    options: {
      placeholder: numberPlaceholder,
      required: false,
    },
  },

  //! WithdrawalsCard
  clientWithdrawals: {
    type: "radio",
    key: "clientWithdrawals",
    name: "clientWithdrawals",
    isVisible: false,
    layout: "horizontal",
    fieldIndex: "3",
    options: {
      required: false,
      options: [
        {
          label:
            "Yes - Client will be withdrawing interest and coupon payments",
          subtext: "(Advisor has/will set up withdrawals with the custodian)",
          value: "yes",
        },
        {
          label: "No",
          value: "no",
        },
      ],
    },
  },

  //! AdditionalNotesCard
  additionalNotes: {
    type: "textarea",
    key: "additionalNotes",
    name: "additionalNotes",
    isVisible: true,
    options: {
      placeholder: "Enter your additional comments here",
      required: false,
      disabled: false,
      rows: 4,
    },
  },

  //! SignAndCompleteCard
  financialRepSignature: {
    type: "text",
    key: "financialRepSignature",
    name: "financialRepSignature",
    label: "Signature of Financial Representative",
    isVisible: true,
    options: {
      placeholder: "Signature of Financial Representative",
      required: true,
      maxLength: maxLengthValidation(50),
      onChange,
    },
  },
  submittedDate: {
    type: "text",
    key: "submittedDate",
    name: "submittedDate",
    label: "Today's Date",
    isVisible: true,
    options: {
      placeholder: "Today's Date",
      required: true,
      readOnly: true,
      isIconLock: true,
      maxLength: maxLengthValidation(20),
    },
  },
};

const DEFAULT_FIELDS_STATES_BY_PAGE_MODE = {
  [PAGE_MODES.create]: DEFAULT_FIELDS_STATES,
  [PAGE_MODES.createWithProposal]: {
    ...DEFAULT_FIELDS_STATES,

    strategyName: {
      ...DEFAULT_FIELDS_STATES.strategyName,
      isIconLock: true,
      options: {
        ...DEFAULT_FIELDS_STATES.strategyName.options,
        readOnly: true,
      },
    },
    statePreferenceOption: {
      ...DEFAULT_FIELDS_STATES.statePreferenceOption,
      isIconLock: true,
      options: {
        ...DEFAULT_FIELDS_STATES.statePreferenceOption.options,
        readOnly: true,
      },
    },
    transitionPlanReportId: {
      ...DEFAULT_FIELDS_STATES.transitionPlanReportId,
      isVisible: true,
    },
    selectScenario: {
      ...DEFAULT_FIELDS_STATES.selectScenario,
      isVisible: true,
    },
  },
  [PAGE_MODES.viewDetails]: Object.fromEntries(
    Object.entries(DEFAULT_FIELDS_STATES).map(([fieldName, fieldStates]) => [
      fieldName,
      {
        ...fieldStates,
        isIconLock: true,
        options: {
          ...fieldStates.options,
          readOnly: true,
        },
      },
    ])
  ),

  [PAGE_MODES.invalid]: {},
};

export const INLINE_DISCLOSURE =
  "Allspring Global Investments assumes no responsibility for any securities other than individual bonds applicable to the chosen strategy held in any separately managed account. Unless specifically instructed, any securities deemed inapplicable for the strategy selected may be liquidated and the proceeds reinvested into the strategy at the discretion of Allspring Global Investments. If no market can be found for the inapplicable securities Allspring will request they be transferred out of the account.";

export const convertApiValueToRadioButtonValue = (field) => {
  if (field === undefined || field === "") return "";
  return field === "true" || field === true ? "yes" : "no";
};
export const convertRadioButtonValueToApiValue = (field) => {
  if (field === undefined || field === "") return undefined;
  return field === "yes";
};
export const convertTaxLossHarvestingFormValueToApiValue = (field) =>
  field === "yes" ? "true" : "false";

export const mapApiDataToClientQuestionnaireValues = ({
  apiData = {},
  pageMode,
  proposal = {},
  genericReferenceData = {},
}) => {
  const {
    advisorEmail,
    advisorName,
    advisorPhoneNum,
    changeToRemiIntakeForm,
    clientName,
    clientWithdrawals,
    createdByEmail,
    createdByName,
    custAccountNumber,
    financialRepSignature,
    groupName,
    notes,
    questionnaireId,
    reportId,
    scenario,
    sponsorName,
    state_pref,
    status,
    strategyName,
    submittedDate,
    taxLossHarvesting,
    transitionId,
    updatedCashToRaise,
    updatedmaxRealizedSTGainsAllowed,
  } = apiData;

  const statePreferenceOption = state_pref || proposal?.statePreferenceOption;
  //! TODO: Confirm the logic with Product
  //! || sponsorData?.natStateOpt || "National";

  if (pageMode === PAGE_MODES.createWithProposal) {
    return {
      advisorEmail: proposal?.advisorEmail,
      advisorName: proposal?.advisorName,
      advisorPhoneNum,
      clientName: proposal?.account,
      createdByEmail,
      createdByName,
      groupName,
      proposalId: transitionId,
      reportId,
      sponsorName,
      statePreferenceOption,
      strategyName: proposal?.strategyName,
      transitionPlanReportId: reportId,
    };
  }

  return {
    accountNumber: custAccountNumber,
    additionalNotes: notes,
    advisorEmail,
    advisorName,
    advisorPhoneNum,
    clientName,
    clientWithdrawals: convertApiValueToRadioButtonValue(clientWithdrawals),
    createdByEmail,
    createdByName,
    financialRepSignature,
    groupName,
    proposalId: transitionId,
    questionnaireId,
    remiIntakeChanged: convertApiValueToRadioButtonValue(
      changeToRemiIntakeForm
    ),
    reportId,
    selectScenario: scenario,
    sponsorName,
    statePreferenceOption: state_pref,
    status,
    strategyName,
    submittedDate: convertDate(getFormattedDate(submittedDate)),
    taxLossHarvesting: convertApiValueToRadioButtonValue(taxLossHarvesting),
    transitionPlanReportId: reportId,
    updatedCashToRaise: updatedCashToRaise ? parseInt(updatedCashToRaise) : "",
    updatedmaxRealizedSTGainsAllowed: updatedCashToRaise
      ? parseInt(updatedmaxRealizedSTGainsAllowed)
      : "",
  };
};

export const mapClientQuestionnaireFormValuesToApiData = ({
  formValues = {},
}) => {
  const {
    accountNumber,
    additionalNotes,
    advisorEmail,
    advisorName,
    advisorPhoneNum,
    clientName,
    clientWithdrawals,
    createdByEmail,
    createdByName,
    financialRepSignature,
    proposalId,
    remiIntakeChanged,
    selectScenario,
    statePreferenceOption,
    strategyName,
    taxLossHarvesting,
    updatedCashToRaise,
    updatedmaxRealizedSTGainsAllowed,
  } = formValues;

  return {
    advisorEmail,
    advisorName,
    advisorPhoneNum,
    changeToRemiIntakeForm:
      convertRadioButtonValueToApiValue(remiIntakeChanged),
    clientName,
    clientWithdrawals: convertRadioButtonValueToApiValue(clientWithdrawals),
    createdByEmail,
    createdByName,
    custAccountNumber: accountNumber,
    financialRepSignature,
    notes: additionalNotes,
    scenario: selectScenario,
    state_pref: statePreferenceOption,
    strategyName,
    taxLossHarvesting:
      convertTaxLossHarvestingFormValueToApiValue(taxLossHarvesting),
    transitionId: proposalId,
    updatedCashToRaise,
    updatedmaxRealizedSTGainsAllowed,
  };
};

export const initializeClientQuestionnaireFormCards = ({ pageMode }) => {
  return Object.fromEntries(
    Object.entries(DEFAULT_CARDS_METADATA).map(([cardName, metadata]) => [
      cardName,
      {
        ...metadata,
        isLocked: DEFAULT_CARDS_LOCKED_STATE_BY_PAGE_MODE[pageMode][cardName],
      },
    ])
  );
};

const updateRadioButtonSelectedOption = ({ options, selected }) => {
  if (!options) return {};

  return {
    options: options.map(({ label, subtext, value }) => ({
      label,
      subtext,
      value,
      ...(selected === value && { checked: true }),
    })),
  };
};

const getNewFieldState = ({ fieldStates, formValues, fieldName }) => ({
  ...fieldStates,
  options: {
    ...fieldStates.options,
    value: formValues[fieldName],
    ...updateRadioButtonSelectedOption({
      options: fieldStates.options.options,
      selected: formValues[fieldName],
    }),
  },
});

export const initializeClientQuestionnaireFormFieldsStates = ({
  pageMode,
  genericReferenceData: { strategyDetails },
  proposal,
  report,
  defaultValues = {},
}) => {
  const defaultFieldStates = DEFAULT_FIELDS_STATES_BY_PAGE_MODE[pageMode];

  /**
   * set visibility for statePreferenceOption depending on statePreferenceOption value
   */
  defaultFieldStates.statePreferenceOption.options.invisible =
    !defaultValues?.statePreferenceOption ||
    defaultValues?.statePreferenceOption === "";

  /**
   * set visibility for updatedCashToRaise and updatedmaxRealizedSTGainsAllowed depending on remiIntakeChanged
   */
  defaultFieldStates.updatedCashToRaise.isVisible =
    defaultValues?.remiIntakeChanged === "yes";
  defaultFieldStates.updatedmaxRealizedSTGainsAllowed.isVisible =
    defaultValues?.remiIntakeChanged === "yes";

  /**
   * set visibility for tax loss harvesting and withdrawals depending on
   * selected strategy
   */
  const strategyClientQuestionnaire = strategyDetails?.filter(
    (strategy) => strategy.strategyName === defaultValues?.strategyName
  )[0]?.clientQuestionnaire;

  defaultFieldStates.taxLossHarvesting.isVisible =
    strategyClientQuestionnaire?.includes("tax-loss-harvesting");
  defaultFieldStates.clientWithdrawals.isVisible =
    strategyClientQuestionnaire?.includes("income-withdrawals");

  /**
   * set submittedDate default value to date today
   */
  if (!defaultValues.submittedDate) {
    defaultValues.submittedDate = convertDate(getFormattedDate(new Date()));
  }

  // Control Select Scenario section given the asset type of report
  defaultFieldStates.selectScenario.options.items =
    report?.assetType === "EQUITY"
      ? PROPOSAL_EQUITY_SCENARIOS
      : PROPOSAL_BOND_SCENARIOS;

  // Set reprortId and transitionPlanReportId value
  if (!defaultValues?.reportId) {
    defaultValues.reportId = report.id;
    defaultValues.transitionPlanReportId = report.id;
  }

  // Automatically set scenario value to "Cash Proposal" for cash report type
  if (report?.reportType === "CASH") {
    defaultValues.selectScenario = "Cash Proposal";
  }
  defaultFieldStates.selectScenario.options.readOnly =
    report?.reportType === "CASH" || pageMode === PAGE_MODES.viewDetails;

  return Object.fromEntries(
    Object.entries(defaultFieldStates).map(([fieldName, fieldStates]) => [
      fieldName,
      {
        ...getNewFieldState({
          fieldStates,
          formValues: defaultValues,
          fieldName,
        }),
      },
    ])
  );
};

export const updateClientQuestionnaireFormFieldsStates = ({
  prevFieldsStates,
  newFormValues = {},
}) => {
  /**
   * set visibility for updatedCashToRaise and updatedmaxRealizedSTGainsAllowed depending on remiIntakeChanged
   */
  prevFieldsStates.updatedCashToRaise.isVisible =
    newFormValues?.remiIntakeChanged === "yes";
  prevFieldsStates.updatedmaxRealizedSTGainsAllowed.isVisible =
    newFormValues?.remiIntakeChanged === "yes";

  return Object.fromEntries(
    Object.entries(prevFieldsStates).map(([fieldName, fieldStates]) => [
      fieldName,
      getNewFieldState({
        fieldStates,
        formValues: newFormValues,
        fieldName,
      }),
    ])
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const initializeProposalCustomizations = (proposal = {}) => {
  const validCustomizations = getValidCustomizations(proposal);
  const customizations = {};

  if (
    validCustomizations.some(
      (customization) => customization.name === "custCreditQuality"
    )
  ) {
    const creditQualityItems = [];
    ["cqSiAAA", "cqSiAA", "cqSiA", "cqSiBBB"].forEach((cq) => {
      if (proposal[cq]) {
        creditQualityItems.push({
          label: cq.replace("cqSi", ""),
          value: `min ${proposal[cq].split("|")[1].split("=")[1]}%, max ${
            proposal[cq].split("|")[2].split("=")[1]
          }%`,
        });
      }
    });
    customizations.creditQuality = creditQualityItems;
  }

  if (
    validCustomizations.some(
      (customization) => customization.name === "custStateExclusions"
    )
  ) {
    customizations.stateExclusions =
      proposal?.templateExclusions
        ?.filter((item) => item?.exclusionType === "State")
        .map((item) => {
          return {
            label: item?.exclusion || "-",
            value: item?.weight
              ? `${(item.weight * 100).toFixed(2).toString()}%`
              : "-",
          };
        }) || [];
  }

  if (
    validCustomizations.some(
      (customization) => customization.name === "custSectorExclusions"
    )
  ) {
    customizations.sectorExclusions =
      proposal?.templateExclusions
        ?.filter((item) => item?.exclusionType === "Sector")
        .map((item) => {
          return {
            label: item?.exclusion || "-",
            value: item?.weight
              ? `${(item.weight * 100).toFixed(2).toString()}%`
              : "-",
          };
        }) || [];
  }

  if (
    validCustomizations.some(
      (customization) => customization.name === "custMaturityRange"
    )
  ) {
    customizations.maturity = proposal?.maturitySpecialInstructions
      ? `${
          proposal.maturitySpecialInstructions === "N/A"
            ? proposal.maturitySpecialInstructions
            : proposal.maturitySpecialInstructions + " years"
        }`
      : "-";
  }

  if (
    validCustomizations.some(
      (customization) => customization.name === "custStatePreference"
    )
  ) {
    customizations.statePreferenceOption =
      proposal.statePreferenceOption || "-";
  }

  return customizations;
};

export const initializeProposalSecurities = (proposal = {}) => {
  const { templateSecRestriction } = proposal;

  if (!templateSecRestriction) return [];

  const mergedDataByCusip = Object.values(
    templateSecRestriction.reduce((acc, curr) => {
      const { cusip } = curr;

      const filteredCurr = Object.keys(curr)
        .filter((key) => curr[key] !== null)
        .reduce((a, k) => ({ ...a, [k]: curr[k] }), {});

      acc[cusip] = { ...acc[cusip], ...filteredCurr };
      return acc;
    }, {})
  );

  return (
    mergedDataByCusip
      .filter((item) => item?.restrictBuy)
      .map((item) => {
        return {
          cusip: item?.cusip || "-",
          minExp: item?.minExposure ? parseInt(item?.minExposure) : 0,
          maxExp: item?.maxExposure ? parseInt(item?.maxExposure) : 0,
        };
      }) || []
  );
};

export const initializeOutputReportDetails = (outputReportDetails = {}) => {
  return {
    id: outputReportDetails.Cover["ReportId"],
    reportType: outputReportDetails.Cover["Report Type"],
    assetType: outputReportDetails.Cover["Asset Type"],
  };
};

export const searchClientQuestionnaires = (items, keyword = "") => {
  return filterListByKeywordGivenKeyNames(items, keyword, [
    "name",
    "advisor",
    "client",
    "strategy",
    "sector",
    "reportId",
    "questionnaireId",
  ]);
};
